<template>
  <div class="w-full max-w-2xl" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div v-if="phase === 'select'" class="space-y-3">
      <button
        v-for="option in knowledgeOptions"
        :key="option.id"
        type="button"
        @click="selectKnowledge(option.id)"
        class="w-full p-4 border rounded-2xl transition-colors text-left shadow-sm hover:shadow-md bg-white/90 dark:bg-slate-900/70"
        :class="selectedKnowledge === option.id ? 'border-stone-400 dark:border-stone-500 bg-stone-100 dark:bg-stone-800' : 'border-stone-200 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800/70'"
        :style="isRtl === true ? { textAlign: 'right' } : { textAlign: 'left' }"
      >
        <div class="flex items-start" :class="isRtl === true ? 'flex-row-reverse' : 'flex-row'">
          <span class="w-10 h-10 rounded-xl bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 flex items-center justify-center flex-shrink-0" :class="isRtl === true ? 'ml-3' : 'mr-3'">
            <Icon :icon="option.icon" class="text-stone-500 dark:text-stone-300" width="20" />
          </span>
          <div class="flex-1 min-w-0">
            <div class="font-medium text-slate-800 dark:text-stone-50">{{ option.title }}</div>
            <div class="text-sm text-slate-600 dark:text-slate-400 mt-1 leading-6">{{ option.description }}</div>
          </div>
        </div>
      </button>
    </div>
    <AgentCreateUnstructuredSources
      v-if="phase === 'sources'"
      ref="sourcesRef"
      @sources-change="onSourcesChange"
    />
    <AgentCreateQuestionAnswerStep
      v-if="phase === 'sources' && selectedKnowledge === 'all'"
      class="mt-5"
      @update:entries="onQuestionAnswersUpdate"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import AgentCreateUnstructuredSources from '@/components/agentCreate/AgentCreateUnstructuredSources.vue';
import AgentCreateQuestionAnswerStep from '@/components/agentCreate/AgentCreateQuestionAnswerStep.vue';

const emit = defineEmits(['submit', 'select', 'advanceSources', 'sourcesSummary']);

const { t, currentLanguage, availableLanguages } = useI18n();
const sourcesRef = ref(null);
const selectedKnowledge = ref('');
const phase = ref('select');
const questionAnswers = ref([]);

const isRtl = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

const knowledgeOptions = computed(() => [
  {
    id: 'qa',
    title: t('agentCreate.knowledge.qa.title', 'Question and answer only agent'),
    description: t('agentCreate.knowledge.qa.description', 'Most accurate, has exactly the info it needs to answer, very easy to maintain and fastest to deploy.'),
    icon: 'tabler:message-question'
  },
  {
    id: 'unstructured',
    title: t('agentCreate.knowledge.unstructured.title', 'Unstructured based knowledge agent'),
    description: t('agentCreate.knowledge.unstructured.description', 'Accurate but needs more fine tuning. Upload websites, URLs, PDF, Word documents and more. May need rules for how to process and answer, plus organization basis info.'),
    icon: 'tabler:file-text'
  },
  {
    id: 'all',
    title: t('agentCreate.knowledge.all.title', 'All in one'),
    description: t('agentCreate.knowledge.all.description', 'All features in one go — Q&A, unstructured sources, and organization context together.'),
    icon: 'tabler:stack-2'
  }
]);

function needsSourcesPhase(knowledgeType) {
  return knowledgeType === 'unstructured' || knowledgeType === 'all';
}

function resolveLabel() {
  const match = knowledgeOptions.value.find((item) => item.id === selectedKnowledge.value);
  if (match !== undefined) {
    return match.title;
  }
  return '';
}

function resolveSources() {
  if (phase.value !== 'sources') {
    return [];
  }
  if (sourcesRef.value === null || sourcesRef.value === undefined) {
    return [];
  }
  if (typeof sourcesRef.value.getSources === 'function') {
    return sourcesRef.value.getSources();
  }
  return [];
}

function selectKnowledge(id) {
  selectedKnowledge.value = id;
  emit('select', {
    knowledgeType: id,
    label: resolveLabel()
  });
}

function onSourcesChange(summary) {
  emit('sourcesSummary', summary);
}

function onQuestionAnswersUpdate(entries) {
  questionAnswers.value = Array.isArray(entries) === true ? entries : [];
}

function submit() {
  if (selectedKnowledge.value === '') {
    return;
  }
  if (phase.value === 'select') {
    if (needsSourcesPhase(selectedKnowledge.value) === true) {
      phase.value = 'sources';
      emit('advanceSources', {
        knowledgeType: selectedKnowledge.value,
        label: resolveLabel()
      });
      return;
    }
    emit('submit', {
      knowledgeType: selectedKnowledge.value,
      label: resolveLabel(),
      sources: [],
      questionAnswers: []
    });
    return;
  }
  emit('submit', {
    knowledgeType: selectedKnowledge.value,
    label: resolveLabel(),
    sources: resolveSources(),
    questionAnswers: questionAnswers.value
  });
}

function isReady() {
  if (selectedKnowledge.value === '') {
    return false;
  }
  if (phase.value === 'select') {
    return true;
  }
  if (sourcesRef.value === null || sourcesRef.value === undefined) {
    return false;
  }
  if (typeof sourcesRef.value.hasSources === 'function') {
    return sourcesRef.value.hasSources() === true;
  }
  return false;
}

function getPhase() {
  return phase.value;
}

defineExpose({
  submit,
  isReady,
  getPhase
});
</script>
